import React, { useState, useEffect, useCallback, memo } from "react";
import {
  Plus,
  Search,
  Edit,
  UserCheck,
  UserX,
  Users,
  Shield,
  User,
  X,
  Check,
  Mail,
  Phone,
} from "lucide-react";
import ApiService from "../../../services/api";

interface User {
  clinic_id: number;
  created_at: string;
  email: string;
  first_name: string;
  is_active: boolean;
  last_name: string;
  phone_number: string;
  role: string;
  status: string;
  two_fa_preference: string | null;
  user_id: number;
}

interface UsersTabProps {
  clinicId: number;
  isAddUser?: boolean;
}

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

// Separate UserForm component to prevent re-renders
const UserForm = memo(
  ({
    initialData,
    onSubmit,
    onCancel,
    isSubmitting,
    isEditing,
  }: {
    initialData: UserFormData;
    onSubmit: (data: UserFormData) => void;
    onCancel: () => void;
    isSubmitting: boolean;
    isEditing: boolean;
  }) => {
    const [formData, setFormData] = useState<UserFormData>(initialData);

    // Reset form data when initialData changes
    useEffect(() => {
      setFormData(initialData);
    }, [initialData]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit(formData);
    };

    const updateFirstName = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, first_name: e.target.value }));
    };

    const updateLastName = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, last_name: e.target.value }));
    };

    const updateEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, email: e.target.value }));
    };

    const updatePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, phone_number: e.target.value }));
    };

    const isFormValid =
      formData.first_name && formData.last_name && formData.email;

    return (
      <form onSubmit={handleSubmit}>
        <div className="space-y-4 sm:space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              value={formData.first_name}
              onChange={updateFirstName}
              className="block w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
              placeholder="Enter first name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              value={formData.last_name}
              onChange={updateLastName}
              className="block w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
              placeholder="Enter last name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={updateEmail}
                className={`block w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base ${
                  isEditing ? "cursor-not-allowed" : ""
                }`}
                placeholder="Enter email address"
                required
                disabled={isEditing}
              />
            </div>
            {isEditing && (
              <p className="mt-1 text-xs text-gray-500">
                Email cannot be changed for security reasons
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="tel"
                value={formData.phone_number}
                onChange={updatePhoneNumber}
                className="block w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
                placeholder="Enter phone number"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !isFormValid}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isEditing ? "Updating..." : "Adding..."}
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {isEditing ? "Update User" : "Add User"}
              </>
            )}
          </button>
        </div>
      </form>
    );
  }
);

const UsersTab: React.FC<UsersTabProps> = ({ clinicId, isAddUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Form data for modals
  const [modalFormData, setModalFormData] = useState<UserFormData>({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter]);

  // Fetch users when dependencies change
  useEffect(() => {
    fetchUsers();
  }, [clinicId, currentPage, statusFilter, debouncedSearchTerm]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: any = {
        clinic_id: clinicId,
        page: currentPage,
        pageSize: pageSize,
      };

      if (debouncedSearchTerm.trim()) {
        params.search_text = debouncedSearchTerm.trim();
      }

      if (statusFilter !== "all") {
        params.is_active = statusFilter === "active" ? 1 : 0;
      }

      const result = await ApiService.searchClinicUser(params);

      if (result.success && result.data) {
        setUsers(result.data);
      } else {
        setError(result.message || "Failed to fetch users");
      }
    } catch (err: any) {
      console.error("Error fetching users:", err);
      setError(
        err?.response?.data?.message || err?.message || "Failed to fetch users"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async (formData: UserFormData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const result = await ApiService.createClinicUser({
        clinic_id: clinicId,
        ...formData,
      });

      if (result.success) {
        setIsAddModalOpen(false);
        fetchUsers();
      } else {
        setError(result.message || "Failed to add user");
      }
    } catch (err: any) {
      console.error("Error adding user:", err);
      setError(
        err?.response?.data?.message || err?.message || "Failed to add user"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async (formData: UserFormData) => {
    if (!selectedUser) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const result = await ApiService.updateClinicUser({
        clinic_id: clinicId,
        user_id: selectedUser.user_id,
        ...formData,
      });

      if (result.success) {
        setIsEditModalOpen(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        setError(result.message || "Failed to update user");
      }
    } catch (err: any) {
      console.error("Error updating user:", err);
      setError(
        err?.response?.data?.message || err?.message || "Failed to update user"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusAction = async (
    userId: number,
    action: "activate" | "deactivate"
  ) => {
    try {
      setError(null);

      const result = await ApiService.updateClinicUserStatus({
        clinic_id: clinicId,
        user_id: userId,
        action: action,
      });

      if (result.success) {
        fetchUsers();
      } else {
        setError(result.message || `Failed to ${action} user`);
      }
    } catch (err: any) {
      console.error(`Error ${action}ing user:`, err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          `Failed to ${action} user`
      );
    }
  };

  const openAddModal = useCallback(() => {
    setModalFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
    });
    setIsAddModalOpen(true);
  }, []);

  useEffect(() => {
    if (isAddUser) {
      openAddModal();
    }
  }, [isAddUser, openAddModal]);

  const openEditModal = useCallback((user: User) => {
    setSelectedUser(user);
    setModalFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone_number: user.phone_number || "",
    });
    setIsEditModalOpen(true);
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  const handleStatusFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value as "all" | "active" | "inactive");
    },
    []
  );

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "admin":
        return <Shield className="h-4 w-4" />;
      case "clinic":
        return <Users className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "clinic":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Filter users based on search and status
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      `${user.first_name} ${user.last_name}`
        .toLowerCase()
        .includes(debouncedSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && user.is_active) ||
      (statusFilter === "inactive" && !user.is_active);
    return matchesSearch && matchesStatus;
  });

  // Modal Component
  const Modal = ({
    isOpen,
    onClose,
    title,
    children,
  }: {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
  }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div
          className="fixed inset-0 bg-black/7 backdrop-blur-sm transition-all duration-300 ease-out"
          onClick={onClose}
        />
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div
            className="relative transform overflow-hidden rounded-2xl bg-white px-4 py-4 text-left shadow-2xl transition-all duration-300 ease-out sm:my-8 sm:w-full sm:max-w-md sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 leading-6">
                {title}
              </h3>
              <button
                onClick={onClose}
                className="rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div>{children}</div>
          </div>
        </div>
      </div>
    );
  };

  // Mobile-friendly User Card Component
  const UserCard = ({ user }: { user: User }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-blue-700">
              {user.first_name.charAt(0)}
              {user.last_name.charAt(0)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="text-sm font-semibold text-gray-900">
                {user.first_name} {user.last_name}
              </h3>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(
                  user.role
                )}`}
              >
                <span className="mr-1">{getRoleIcon(user.role)}</span>
                {user.role.toUpperCase()}
              </span>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-gray-600 flex items-center">
                <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">{user.email}</span>
              </div>
              {user.phone_number && (
                <div className="text-sm text-gray-600 flex items-center">
                  <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span>{user.phone_number}</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-3">
              <div className="flex flex-col space-y-1">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    user.is_active
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {user.is_active ? "Active" : "Inactive"}
                </span>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(
                    user.status
                  )}`}
                >
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </span>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Created: {formatDate(user.created_at)}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-3">
          <button
            onClick={() => openEditModal(user)}
            className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit user"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() =>
              handleStatusAction(
                user.user_id,
                user.is_active ? "deactivate" : "activate"
              )
            }
            className={`p-2 rounded-lg transition-colors ${
              user.is_active
                ? "text-red-600 hover:text-red-900 hover:bg-red-50"
                : "text-green-600 hover:text-green-900 hover:bg-green-50"
            }`}
            title={user.is_active ? "Deactivate user" : "Activate user"}
          >
            {user.is_active ? (
              <UserX className="h-4 w-4" />
            ) : (
              <UserCheck className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
            Users
          </h3>
          <p className="text-sm sm:text-base text-gray-600">
            Manage users for this account
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex justify-between items-start">
            <div className="text-sm text-red-700">{error}</div>
            <button
              onClick={() => setError(null)}
              className="text-red-700 hover:text-red-900 ml-4"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-4 sm:mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                        <span className="text-sm font-medium text-blue-700">
                          {user.first_name.charAt(0)}
                          {user.last_name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          {user.email}
                        </div>
                        {user.phone_number && (
                          <div className="text-sm text-gray-500 flex items-center">
                            <Phone className="h-3 w-3 mr-1" />
                            {user.phone_number}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(
                        user.role
                      )}`}
                    >
                      <span className="mr-1">{getRoleIcon(user.role)}</span>
                      {user.role.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {user.is_active ? "Active" : "Inactive"}
                      </span>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          user.status
                        )}`}
                      >
                        {user.status.charAt(0).toUpperCase() +
                          user.status.slice(1)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(user)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Edit user"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() =>
                          handleStatusAction(
                            user.user_id,
                            user.is_active ? "deactivate" : "activate"
                          )
                        }
                        className={`p-1 ${
                          user.is_active
                            ? "text-red-600 hover:text-red-900"
                            : "text-green-600 hover:text-green-900"
                        }`}
                        title={
                          user.is_active ? "Deactivate user" : "Activate user"
                        }
                      >
                        {user.is_active ? (
                          <UserX className="h-4 w-4" />
                        ) : (
                          <UserCheck className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile/Tablet Card View */}
        <div className="lg:hidden space-y-4">
          {filteredUsers.map((user) => (
            <UserCard key={user.user_id} user={user} />
          ))}
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12 px-4">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No users found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {debouncedSearchTerm
                ? "Try adjusting your search."
                : "Get started by adding your first user."}
            </p>
            {!debouncedSearchTerm && (
              <button
                onClick={openAddModal}
                className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add User Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New User"
      >
        <UserForm
          initialData={modalFormData}
          onSubmit={handleAddUser}
          onCancel={() => setIsAddModalOpen(false)}
          isSubmitting={isSubmitting}
          isEditing={false}
        />
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit User"
      >
        <UserForm
          initialData={modalFormData}
          onSubmit={handleUpdateUser}
          onCancel={() => setIsEditModalOpen(false)}
          isSubmitting={isSubmitting}
          isEditing={true}
        />
      </Modal>
    </div>
  );
};

export default UsersTab;
